import type { DemoAction } from './actions.ts';
import { applyDemoAction, createInitialState, normalizeState, persistedStateFromSnapshot, snapshotFromState } from './state.ts';
import type { DemoPersistedState, DemoSnapshot } from './types.ts';

const STORAGE_KEY = 'field-service-technique-demo-v1';
const SNAPSHOT_CACHE_KEY = 'field-service-technique-demo-snapshot-cache-v1';

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readState(): DemoPersistedState {
  if (!canUseStorage()) return createInitialState();

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const initialState = createInitialState();
    writeState(initialState);
    return initialState;
  }

  try {
    return normalizeState(JSON.parse(raw));
  } catch {
    const initialState = createInitialState();
    writeState(initialState);
    return initialState;
  }
}

function writeState(state: DemoPersistedState): DemoPersistedState {
  if (canUseStorage()) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
  return state;
}

function readSnapshotCache(): DemoSnapshot | null {
  if (!canUseStorage()) return null;

  const raw = window.localStorage.getItem(SNAPSHOT_CACHE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as DemoSnapshot;
    if (!parsed || typeof parsed !== 'object' || !('flowState' in parsed) || !('workOrders' in parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSnapshotCache(snapshot: DemoSnapshot): DemoSnapshot {
  if (canUseStorage()) {
    window.localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify(snapshot));
  }
  return snapshot;
}

function updateState(mutator: (state: DemoPersistedState) => DemoPersistedState): DemoSnapshot {
  const nextState = mutator(readState());
  writeState(nextState);
  return writeSnapshotCache(snapshotFromState(nextState));
}

export function getDemoSnapshotSync(): DemoSnapshot {
  return writeSnapshotCache(snapshotFromState(readState()));
}

export function getCachedDemoSnapshotSync(): DemoSnapshot | null {
  return readSnapshotCache();
}

export async function fetchDemoSnapshot(): Promise<DemoSnapshot> {
  return getDemoSnapshotSync();
}

export function replaceLocalDemoSnapshot(snapshot: DemoSnapshot): DemoSnapshot {
  writeState(persistedStateFromSnapshot(snapshot));
  return writeSnapshotCache(snapshot);
}

export async function runLocalDemoAction(action: DemoAction): Promise<DemoSnapshot> {
  return updateState((state) => applyDemoAction(state, action));
}
